/**
 * Tavily Search Adapter
 * Implementation for Tavily API (LLM-optimized search)
 * https://tavily.com
 */

import type { IWebSearchAdapter, WebSearchOptions } from './IWebSearchAdapter';
import type {
  TavilySearchConfig,
  WebSearchResponse,
  WebSearchResult,
  RateLimitInfo,
} from '@/types/webSearch.types';
import { getBackendUrl, getAuthHeaders, prepareWebSearchQuery } from './webSearchUtils';

interface TavilyResult {
  title?: string;
  url?: string;
  content?: string;
  published_date?: string;
  score?: number;
}

interface TavilyApiResponse {
  results?: TavilyResult[];
  answer?: string;
}

export class TavilyAdapter implements IWebSearchAdapter {
  public readonly provider = 'tavily';
  private config: TavilySearchConfig | null = null;

  /**
   * Initialize the Tavily adapter with configuration
   */
  async initialize(config: TavilySearchConfig): Promise<void> {
    if (!config.apiKey || config.apiKey.trim() === '') {
      throw new Error('Tavily API key is required');
    }

    if (config.provider !== 'tavily') {
      throw new Error(`Invalid provider: expected 'tavily', got '${config.provider}'`);
    }

    this.config = config;
  }

  /**
   * Execute a web search using Tavily API via backend proxy
   */
  async search(query: string, options?: WebSearchOptions): Promise<WebSearchResponse> {
    if (!this.config) {
      throw new Error('Adapter not initialized. Call initialize() first.');
    }

    if (!query || query.trim() === '') {
      throw new Error('Search query cannot be empty');
    }

    const startTime = Date.now();

    try {
      // Call backend proxy to avoid CORS issues
      const headers = await getAuthHeaders();
      const response = await fetch(`${getBackendUrl()}/web-search`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          provider: 'tavily',
          query: prepareWebSearchQuery(query, 'tavily'),
          originalQueryLength: query.trim().length,
          config: {
            apiKey: this.config.apiKey,
            searchDepth: this.config.searchDepth || 'basic',
            maxResults: this.config.maxResults || 5,
            includeAnswer: this.config.includeAnswer ?? false,
            includeRawContent: this.config.includeRawContent ?? false,
            includeDomains: this.config.includeDomains || [],
            excludeDomains: this.config.excludeDomains || [],
          },
        }),
        signal: options?.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Tavily API error (${response.status}): ${errorText || response.statusText}`
        );
      }

      const data = await response.json() as TavilyApiResponse;

      // Normalize Tavily response to our standard format
      const results: WebSearchResult[] = (data.results || []).map((result: TavilyResult) => ({
        title: result.title || '',
        url: result.url || '',
        snippet: result.content || '',
        publishedDate: result.published_date ? new Date(result.published_date) : undefined,
        relevanceScore: typeof result.score === 'number' ? result.score : undefined,
      }));

      return {
        query,
        results,
        totalResults: results.length,
        answer: data.answer || undefined, // Tavily-specific AI-generated answer
        metadata: {
          provider: 'tavily',
          took: Date.now() - startTime,
          cached: false,
        },
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Tavily search failed: ${error.message}`);
      }
      throw new Error('Tavily search failed with unknown error');
    }
  }

  /**
   * Test connection by performing a simple search
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.search('test connection');
      return true;
    } catch (error) {
      console.error('Tavily connection test failed:', error);
      return false;
    }
  }

  /**
   * Tavily doesn't provide rate limit headers
   */
  async getRateLimitInfo(): Promise<RateLimitInfo | null> {
    return null;
  }
}
